/**
 * Pure helpers for the Event Performance "Open for registration" panel
 * (RPC list_open_registration_live_counts, migration 2026-10-12-430).
 *
 * The server returns EVERY cap event-landing-register enforces, each tagged with the registration
 * channels it refuses, plus the channels the event accepts. placesLeft() keeps only the caps that
 * apply to an accepted channel and picks the one that binds (fewest places left), so the panel shows
 * one honest "remaining" number and never calls an event Full that invitees can still register for.
 */

export type OpenRegistrationCapKind =
  | "eib_registration"
  | "capacity"
  | "pre_registration";

/**
 * The two ways a NEW registration reaches event-landing-register:
 *   public     — UNBOUND: no valid personal invite token (`isTokenBound` false).
 *   invitation — BOUND: a valid personal invite token (an invite_gated invitee, or a guest invitation).
 */
export type RegistrationChannel = "public" | "invitation";

export const REGISTRATION_CHANNELS: readonly RegistrationChannel[] = [
  "public",
  "invitation",
];

/**
 * Which channels each cap REFUSES — mirrors event-landing-register exactly (and migration 430's
 * `applies_to`, which the server sends; this table is only the fallback for a payload without it):
 *   capacity          — no channel condition: refuses public AND invitation registrations.
 *   eib_registration  — open_issuance_cap, checked whenever mode = 'open': both channels.
 *   pre_registration  — checked only when `!isTokenBound`: public sign-ups ONLY.
 */
export const CAP_APPLIES_TO: Record<
  OpenRegistrationCapKind,
  readonly RegistrationChannel[]
> = {
  eib_registration: ["public", "invitation"],
  capacity: ["public", "invitation"],
  pre_registration: ["public"],
};

/**
 * Channels that can still create a NEW registration. The handler's invitation-only guard refuses
 * every unbound registration on an invite_gated event; every other mode takes both.
 */
export function acceptedChannelsForMode(
  registrationMode: string | null | undefined,
): RegistrationChannel[] {
  return registrationMode === "invite_gated"
    ? ["invitation"]
    : ["public", "invitation"];
}

export interface OpenRegistrationCap {
  kind: OpenRegistrationCapKind;
  limit: number;
  used: number;
  /** Channels this cap refuses (server-provided; falls back to CAP_APPLIES_TO[kind]). */
  applies_to?: readonly RegistrationChannel[];
}

export interface BindingCap extends OpenRegistrationCap {
  remaining: number;
  full: boolean;
}

export const CAP_LABELS: Record<OpenRegistrationCapKind, string> = {
  // event_ticketing_provisioning.open_issuance_cap — counts tickets, including door tickets.
  eib_registration: "Online registration limit",
  // max_attendees (hard cap) / capacity — people, registered + attended.
  capacity: "Capacity",
  // pre_registration_cap — refuses only PUBLIC (unbound) sign-ups; invitation links bypass it.
  pre_registration: "Public sign-up limit",
};

/** What each cap counts — they are not all the same unit. */
export const CAP_UNITS: Record<OpenRegistrationCapKind, string> = {
  // A ticket per registration (and per door admission), not per person in a party.
  eib_registration: "tickets issued, including at the door",
  capacity: "people registered or attended",
  // Invited registrations are landing-page rows too, so they count toward it, but it never refuses them.
  pre_registration:
    "people pre-registered online, invited registrations included; it refuses public sign-ups only, never a valid personal invitation link",
};

/**
 * The cap with the fewest places left, or null when the event is uncapped.
 * Ties keep the server's order (kinds sorted alphabetically), so the result is deterministic.
 * Channel-blind: callers pass only the caps that apply (see placesLeft).
 */
export function bindingCap(
  caps: readonly OpenRegistrationCap[] | null | undefined,
): BindingCap | null {
  let best: BindingCap | null = null;
  for (const cap of caps ?? []) {
    if (!Number.isFinite(cap.limit) || !Number.isFinite(cap.used)) {
      continue;
    }
    const remaining = Math.max(cap.limit - cap.used, 0);
    if (best === null || remaining < best.remaining) {
      best = { ...cap, remaining, full: remaining <= 0 };
    }
  }
  return best;
}

// An empty or missing server `applies_to` falls back to the handler table (the server never sends []).
export const capAppliesTo = (
  cap: OpenRegistrationCap,
): readonly RegistrationChannel[] =>
  cap.applies_to && cap.applies_to.length > 0
    ? cap.applies_to
    : CAP_APPLIES_TO[cap.kind];

export type PlacesLeftState =
  /** No cap applies to any channel the event accepts. */
  | { state: "no_limit" }
  /** Every accepted channel is closed — nobody new can register. */
  | { state: "full"; cap: BindingCap }
  /** Public sign-ups are closed but personal invitation links still register (`invitation` = the
   *  tightest cap left on that channel, or null when none applies to it). */
  | { state: "public_full"; cap: BindingCap; invitation: BindingCap | null }
  /** Still open: `cap` is the tightest cap among those that apply. */
  | { state: "open"; cap: BindingCap };

/**
 * What the "Places left" cell should say, using ONLY caps that apply to a channel the event accepts
 * (external audit 93d4320d P2: an invite-only event was shown "Full" by the public pre-registration
 * cap, which the handler never applies to invitees).
 */
export function placesLeft(row: {
  registration_mode: string | null;
  accepted_channels?: readonly RegistrationChannel[] | null;
  caps: readonly OpenRegistrationCap[] | null | undefined;
}): PlacesLeftState {
  const accepted =
    row.accepted_channels && row.accepted_channels.length > 0
      ? row.accepted_channels
      : acceptedChannelsForMode(row.registration_mode);
  const caps = row.caps ?? [];
  const forChannel = (ch: RegistrationChannel) =>
    bindingCap(caps.filter((c) => capAppliesTo(c).includes(ch)));
  const applicable = caps.filter((c) =>
    capAppliesTo(c).some((ch) => accepted.includes(ch)),
  );
  const overall = bindingCap(applicable);
  if (!overall) {
    return { state: "no_limit" };
  }
  const perChannel = new Map(accepted.map((ch) => [ch, forChannel(ch)]));
  const closed = (ch: RegistrationChannel) => perChannel.get(ch)?.full === true;
  if (accepted.every(closed)) {
    // A cap that closes EVERY accepted channel exists (only all-channel caps close invitations).
    const closingAll = bindingCap(
      applicable.filter((c) =>
        accepted.every((ch) => capAppliesTo(c).includes(ch)),
      ),
    );
    return { state: "full", cap: closingAll?.full ? closingAll : overall };
  }
  if (accepted.includes("public") && closed("public")) {
    return {
      state: "public_full",
      cap: perChannel.get("public") as BindingCap,
      invitation: perChannel.get("invitation") ?? null,
    };
  }
  return { state: "open", cap: overall };
}

/**
 * Change since the panel first saw this event. `baseline` is captured once per event per page
 * visit; an event that first appears mid-session starts at 0.
 */
export function changeSinceOpened(
  baseline: number | undefined,
  current: number,
): number {
  if (baseline === undefined) {
    return 0;
  }
  return current - baseline;
}

/** "+3", "−1", or "" for no change. */
export function formatChange(delta: number): string {
  if (delta > 0) {
    return `+${delta}`;
  }
  if (delta < 0) {
    return `−${Math.abs(delta)}`;
  }
  return "";
}

const FALLBACK_TZ = "America/Chicago";

/**
 * Start in the EVENT's timezone ("Sat, Oct 17 · 9:00 AM CDT"). An unknown or blank timezone falls
 * back to America/Chicago — the same fallback the server-side resolvers use.
 */
export function formatEventStartInZone(
  startIso: string | null,
  timezone: string | null,
): string {
  if (!startIso) {
    return "—";
  }
  const d = new Date(startIso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  const render = (tz: string) =>
    `${d.toLocaleDateString("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" })} · ${d.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", timeZoneName: "short" })}`;
  const tz = timezone?.trim();
  if (tz) {
    try {
      return render(tz);
    } catch {
      // RangeError: invalid time zone — fall through.
    }
  }
  return render(FALLBACK_TZ);
}

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function formatAgo(
  iso: string | null,
  now: number = Date.now(),
): string {
  if (!iso) {
    return "—";
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) {
    return "—";
  }
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) {
    return "just now";
  }
  const m = Math.floor(s / 60);
  if (m < 60) {
    return `${m} min ago`;
  }
  const h = Math.floor(m / 60);
  if (h < 48) {
    return `${h} h ago`;
  }
  return `${Math.floor(h / 24)} d ago`;
}
